<template>
  <div class="wm-overlay" @click.self="close">
    <section
      class="wm-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Withdraw funds"
    >
      <button class="wm-close" type="button" :disabled="loading" @click="close">
        ×
      </button>

      <div class="title medium wm-title">
        {{
          step === 1
            ? 'WITHDRAW'
            : step === 2
              ? 'PAYOUT METHOD'
              : 'SEPA DETAILS'
        }}
      </div>

      <!-- STEP 1: PERSONAL DETAILS -->
      <div v-if="step === 1" class="wm-fields">
        <label>
          Amount ({{ currency }})
          <input
            v-model.trim="form.amount"
            :readonly="continuation"
            type="text"
            inputmode="decimal"
            placeholder="0.00"
          />
        </label>

        <p v-if="balanceKnown" class="wm-muted">
          Available: {{ money(balance) }} {{ currency }}
        </p>

        <label>
          Email
          <input v-model.trim="form.email" type="email" autocomplete="email" />
        </label>

        <div class="wm-grid">
          <label>
            First name
            <input v-model.trim="form.name" autocomplete="given-name" />
          </label>

          <label>
            Last name
            <input v-model.trim="form.surname" autocomplete="family-name" />
          </label>
        </div>

        <label>
          Phone
          <input v-model.trim="form.phone" type="tel" autocomplete="tel" />
        </label>

        <label>
          Country
          <select v-model="form.country">
            <option value="" disabled>Select country</option>

            <option v-for="c in countries" :key="c.id" :value="c.id">
              {{ c.title }}
            </option>
          </select>
        </label>

        <label>
          City
          <input v-model.trim="form.city" autocomplete="address-level2" />
        </label>

        <label>
          Address
          <input v-model.trim="form.address" autocomplete="street-address" />
        </label>

        <label>
          ZIP / Postal code
          <input v-model.trim="form.postCode" autocomplete="postal-code" />
        </label>

        <p v-if="!availableMethods.length" class="wm-error">
          No payout methods available.
        </p>

        <button
          type="button"
          class="wm-button wm-primary"
          :disabled="loading || !step1Valid || !availableMethods.length"
          @click="step = 2"
        >
          NEXT
        </button>
      </div>

      <!-- STEP 2: PAYOUT METHOD -->
      <div v-else-if="step === 2">
        <p class="wm-muted">Choose how you want to receive your withdrawal.</p>

        <div class="wm-methods">
          <button
            v-if="availableMethods.includes('card-form')"
            type="button"
            class="wm-method"
            :disabled="loading"
            @click="choose('card-form')"
          >
            VISA / CARD
          </button>

          <button
            v-if="availableMethods.includes('sepa-manual')"
            type="button"
            class="wm-method"
            :disabled="loading"
            @click="choose('sepa-manual')"
          >
            SEPA BANK
          </button>
        </div>

        <p class="wm-muted">
          {{
            continuation
              ? 'This approved request will be sent to the payout provider.'
              : 'This creates a withdrawal request for admin review.'
          }}
        </p>

        <button
          type="button"
          class="wm-button wm-secondary"
          :disabled="loading"
          @click="step = 1"
        >
          BACK
        </button>
      </div>

      <!-- STEP 3: SEPA BANK DETAILS -->
      <div v-else class="wm-fields">
        <label>
          IBAN
          <input
            v-model.trim="form.account_number"
            autocomplete="off"
            placeholder="IBAN"
          />
        </label>

        <div class="wm-grid">
          <label>
            Account first name
            <input v-model.trim="form.first_name" />
          </label>

          <label>
            Account last name
            <input v-model.trim="form.last_name" />
          </label>
        </div>

        <p class="wm-muted">
          {{
            continuation
              ? 'Your bank details will be sent securely to the payout API.'
              : 'Bank details will be requested again after admin approval.'
          }}
        </p>

        <div class="wm-actions">
          <button
            type="button"
            class="wm-button wm-secondary"
            :disabled="loading"
            @click="step = 2"
          >
            BACK
          </button>

          <button
            type="button"
            class="wm-button wm-primary"
            :disabled="loading || !sepaValid"
            @click="submit"
          >
            {{
              loading
                ? 'SENDING...'
                : continuation
                  ? 'CONTINUE PAYOUT'
                  : 'REQUEST'
            }}
          </button>
        </div>
      </div>

      <p v-if="error" class="wm-error">
        {{ error }}
      </p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'WithdrawModal',

  props: {
    continuation: {
      type: Boolean,
      default: false,
    },
    approvedAmount: {
      type: [String, Number],
      default: null,
    },
    currency: {
      type: String,
      default: 'EUR',
    },

    balance: {
      type: [String, Number],
      default: null,
    },

    enabledMethods: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    error: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      step: 1,
      method: '',
      countries: [],

      form: {
        amount: this.continuation ? String(this.approvedAmount ?? '') : '',
        email: '',
        name: '',
        surname: '',
        phone: '',
        country: '',
        city: '',
        address: '',
        postCode: '',
        account_number: '',
        first_name: '',
        last_name: '',
      },
    }
  },

  computed: {
    availableMethods() {
      return ['card-form', 'sepa-manual'].filter((method) =>
        this.enabledMethods.includes(method),
      )
    },

    balanceKnown() {
      return (
        this.balance !== null &&
        this.balance !== undefined &&
        this.balance !== ''
      )
    },

    validAmount() {
      const text = String(this.form.amount)
      const value = Number(text)

      return (
        /^\d+(\.\d{1,2})?$/.test(text) &&
        value > 0 &&
        (this.continuation ||
          !this.balanceKnown ||
          value <= Number(this.balance))
      )
    },

    step1Valid() {
      const f = this.form

      return (
        this.validAmount &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email) &&
        [
          f.name,
          f.surname,
          f.phone,
          f.country,
          f.city,
          f.address,
          f.postCode,
        ].every((value) => String(value).trim().length > 0)
      )
    },

    sepaValid() {
      const f = this.form

      return (
        /^[A-Z]{2}[0-9A-Z]{13,32}$/.test(
          f.account_number.replace(/\s/g, '').toUpperCase(),
        ) &&
        !!f.first_name &&
        !!f.last_name
      )
    },
  },

  watch: {
    availableMethods(methods) {
      if (this.method && !methods.includes(this.method)) {
        this.method = ''
        this.step = 2
      }

      if (!methods.length) {
        this.step = 1
      }
    },
  },

  mounted() {
    this.$http
      .get(process.env.VUE_APP_API + 'countries')
      .then((response) => {
        const data = response.data || {}
        const items = data.payload || data.data || []

        this.countries = Array.isArray(items)
          ? items
          : Array.isArray(items.data)
            ? items.data
            : []
      })
      .catch(() => {
        this.countries = []
      })
  },

  methods: {
    money(value) {
      return Number(value || 0).toFixed(2)
    },

    close() {
      if (!this.loading) {
        this.$emit('close')
      }
    },

    choose(method) {
      if (
        this.loading ||
        !this.step1Valid ||
        !this.availableMethods.includes(method)
      ) {
        return
      }

      this.method = method

      if (method === 'sepa-manual') {
        this.form.first_name = this.form.first_name || this.form.name

        this.form.last_name = this.form.last_name || this.form.surname

        this.step = 3
      } else {
        this.submit()
      }
    },

    submit() {
      if (
        this.loading ||
        !this.step1Valid ||
        !this.availableMethods.includes(this.method)
      ) {
        return
      }

      if (this.method === 'sepa-manual' && !this.sepaValid) {
        return
      }

      this.$emit('submit', {
        amount: this.form.amount,
        method: this.method,
        details: {
          ...this.form,
        },
      })
    },
  },
}
</script>

<style scoped>
.wm-overlay,
.wm-overlay * {
  box-sizing: border-box;
}

.wm-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  background: rgba(0, 0, 0, 0.84);
}

.wm-panel {
  position: relative;
  width: 100%;
  max-width: 440px;
  max-height: calc(100vh - 28px);
  overflow-y: auto;
  padding: 26px 22px;
  background: #211919;
  border: 1px solid #554040;
  color: #fff;
}

.wm-title {
  margin: 0 30px 22px 0;
}

.wm-close {
  position: absolute;
  top: 9px;
  right: 12px;
  border: 0;
  background: transparent;
  color: #aaa;
  font:
    28px Arial,
    sans-serif;
  cursor: pointer;
}

.wm-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wm-fields label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  font:
    12px 'Open Sans',
    Arial,
    sans-serif;
}

.wm-fields input,
.wm-fields select {
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 8px 10px;
  border: 1px solid #554040;
  border-radius: 0;
  outline: 0;
  background: #171111;
  color: #fff;
  font:
    13px 'Open Sans',
    Arial,
    sans-serif;
}

.wm-fields input:focus,
.wm-fields select:focus {
  border-color: #d94d32;
}

.wm-grid,
.wm-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.wm-muted {
  margin: 6px 0 12px;
  color: #aaa;
  font:
    12px/1.5 'Open Sans',
    Arial,
    sans-serif;
}

.wm-error {
  color: #ff9898;
  font:
    12px/1.5 'Open Sans',
    Arial,
    sans-serif;
}

.wm-methods {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(125px, 1fr));
  gap: 10px;
  margin: 15px 0;
}

.wm-button,
.wm-method {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  min-height: 42px;
  padding: 9px 8px;
  border-radius: 0;
  color: #fff;
  font:
    18px 'Bebas Neue',
    Impact,
    sans-serif;
  cursor: pointer;
}

.wm-primary {
  border: 1px solid #d94d32;
  background: #d94d32;
}

.wm-secondary,
.wm-method {
  border: 1px solid #554040;
  background: #302626;
}

.wm-button:disabled,
.wm-method:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 400px) {
  .wm-panel {
    padding: 24px 15px;
  }

  .wm-button,
  .wm-method {
    font-size: 16px;
  }
}
</style>
